import React, { useContext, useEffect, useRef, useState } from 'react';

import { Error, Lock, MoreHoriz, Save } from '@mui/icons-material';
import {
    type BadgeProps,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    Divider,
    Grid,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    type ListItemButtonProps,
    ListItemIcon,
    type ListItemProps,
    ListItemText,
    ListSubheader,
    Menu,
    MenuItem,
    TextField,
    Typography
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';

import { AddPageButton, PdfHeader } from './interface';
import { ImportPdfButton } from './interface/ImportPdfButton';
import { Status } from '../../../types/apiTypes';
import LinearProgressWithLabel from '../../LinearProgressWithLabel';
import { WhiteboardSessionContext } from '../context/WhiteboardSessionContext';
import { SessionTypes } from '../types';

type Item = {
    name: string;
    is_locked: boolean;
};

type ItemCallback<T> = (item: T) => void;

type ItemActionsProps<T> = {
    index: number;
    item: T;
    open: boolean;
    onClose: () => void;
    onOpen: ItemCallback<T>;
    onRename?: () => void;
    onItemCopy?: ItemCallback<T>;
    onItemLock?: ItemCallback<T>;
    onItemDelete?: ItemCallback<T>;
};

const StyledIconButton = styled(IconButton)`
    width: 2rem;
    height: 2rem;
    align-self: center;
    margin-left: auto;
`;

const ItemActions = <T extends Item>(props: ItemActionsProps<T>) => {
    const { t } = useTranslation(['whiteboard', 'common']);
    const { index, item, open, onOpen, onClose, onRename, onItemCopy, onItemDelete, onItemLock } = props;
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const handleClose = React.useCallback(
        (event: any) => {
            event.stopPropagation();
            event.preventDefault();

            onClose();
        },
        [onClose]
    );

    return (
        <>
            <StyledIconButton
                edge="end"
                aria-controls={`item-menu-${index}`}
                aria-haspopup="true"
                onClick={(event: any) => {
                    event.stopPropagation();
                    event.preventDefault();

                    setAnchorEl(event.currentTarget);
                    onOpen(item);
                }}
                size="large"
                color="primary"
                sx={{ padding: '5px' }}
            >
                {item.is_locked && <Lock />}
                {!item.is_locked && <MoreHoriz />}
            </StyledIconButton>
            <Menu
                id={`item-menu-${index}`}
                anchorEl={anchorEl}
                keepMounted
                open={open}
                onClose={handleClose}
                onClick={handleClose}
            >
                {onItemCopy && (
                    <MenuItem onClick={() => onItemCopy(item)}>
                        <Typography>{t('common:copy')}</Typography>
                    </MenuItem>
                )}
                {onRename && (
                    <MenuItem
                        onClick={() => {
                            onRename();
                        }}
                    >
                        <Typography>{t('common:rename')}</Typography>
                    </MenuItem>
                )}
                {onItemLock && (
                    <MenuItem onClick={() => onItemLock(item)}>
                        <ListItemIcon>
                            <Lock fontSize="small" />
                        </ListItemIcon>
                        {item.is_locked && <Typography>{t('whiteboard:unlockItem')}</Typography>}
                        {!item.is_locked && <Typography>{t('whiteboard:lockItem')}</Typography>}
                    </MenuItem>
                )}
                {onItemDelete && ( // Menu component can't have fragment children. Hence same check below.
                    <Divider />
                )}
                {onItemDelete && (
                    <MenuItem onClick={() => onItemDelete(item)}>
                        <Typography>{t('common:delete')}</Typography>
                    </MenuItem>
                )}
            </Menu>
        </>
    );
};

export type EditableListItemProps<T> = Pick<ItemActionsProps<T>, 'onItemCopy' | 'onItemLock' | 'onItemDelete'> &
    ListItemButtonProps &
    ListItemProps &
    Pick<BadgeProps, 'badgeContent'> & {
        item: T;
        index: number;
        name?: string;
        defaultName?: string;
        onItemRename?: ItemCallback<T>;
    };

const listItemSharedStyles = css`
    border-right: 3px solid transparent;

    &.selected {
        border-right-color: ${(props) => props.theme.colors.primary.main};
    }
`;

const StyledListRenameItem: typeof ListItem = styled(ListItem)`
    ${listItemSharedStyles}

    padding: 0.25rem;
    padding-left: 0.5rem;
`;

const StyledListItem: typeof ListItem = styled(ListItem)`
    ${listItemSharedStyles}

    display: contents;
`;

const StyledListItemText: typeof ListItemText = styled(ListItemText)`
    margin-left: 1rem;
    word-break: break-all;
`;

const StyledListItemButton: typeof ListItemButton = styled(ListItemButton)`
    ${listItemSharedStyles}
`;

const StyledListItemSaveButton: typeof ListItemButton = styled(ListItemButton)`
    min-width: 2.5rem;
    height: 2.5rem;
    padding: 0;
    margin-left: 0.5rem;
    border-radius: 0.25rem;
`;

const StyledListItemSaveIcon: typeof ListItemIcon = styled(ListItemIcon)`
    min-width: 0;
    padding: 8px;
`;

const StyledListHeader = styled(ListSubheader)`
    line-height: 1;
`;

const StyledPreviewImage = styled('img')`
    border: 2px solid rgb(230, 230, 230);
`;

const StyledSelectedPreviewImage = styled('img')`
    border: 2px solid #4739e7;
`;

export const EditableListItem = <T extends Item>(props: EditableListItemProps<T>) => {
    const {
        badgeContent,
        defaultName,
        disabled,
        index,
        name,
        item,
        selected,
        onClick,
        onItemCopy,
        onItemDelete,
        onItemLock,
        onItemRename,
        ...listItemProps
    } = props;

    const { t } = useTranslation('whiteboard');

    const itemName = name || item.name || defaultName;
    const [renaming, setRenaming] = useState<boolean>(false);
    const [actionMenuOpen, setActionMenuOpen] = useState<boolean>(false);
    const renameFieldRef = useRef<HTMLInputElement>();

    const handleRenameItem = () => {
        const newName = (renameFieldRef.current?.value ?? '').trim();

        if (onItemRename && newName.length > 0) {
            item.name = newName;
            setRenaming(false);
            onItemRename(item);
        }
    };

    const handleLockItem = () => {
        if (onItemLock) {
            item.is_locked = !item.is_locked;
            onItemLock(item);
        }
    };

    const showActions = !disabled && (onItemRename || onItemCopy || onItemLock || onItemDelete);

    useEffect(() => {
        if (renaming && !!renameFieldRef.current) {
            renameFieldRef.current.focus();
        }
    }, [renaming]);

    if (renaming) {
        return (
            <StyledListRenameItem {...listItemProps}>
                <TextField
                    inputRef={renameFieldRef}
                    hiddenLabel
                    defaultValue={item.name}
                    variant="outlined"
                    size="small"
                    onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                            handleRenameItem();
                        }
                    }}
                />

                <StyledListItemSaveButton onClick={handleRenameItem} aria-label={t('save')}>
                    <StyledListItemSaveIcon>
                        <Save />
                    </StyledListItemSaveIcon>
                </StyledListItemSaveButton>
            </StyledListRenameItem>
        );
    }

    const backgroundElement = (item as any).data?.backgroundElement;
    const previewBackgroundUrl = backgroundElement ? backgroundElement.body.previewUrl : undefined;

    return (
        <StyledListItem {...listItemProps}>
            <StyledListItemButton
                disabled={disabled}
                disableRipple={false}
                className={selected ? 'selected' : ''}
                onClick={onClick}
            >
                {!previewBackgroundUrl && <small>{badgeContent}</small>}
                {previewBackgroundUrl && !selected && <StyledPreviewImage src={previewBackgroundUrl} width={130} />}
                {previewBackgroundUrl && selected && (
                    <StyledSelectedPreviewImage src={previewBackgroundUrl} width={130} />
                )}
                {!previewBackgroundUrl && (
                    <StyledListItemText
                        primary={
                            <Typography fontWeight={selected ? 800 : 400} color={selected ? 'primary' : 'secondary'}>
                                {itemName}
                            </Typography>
                        }
                    />
                )}
                {showActions && (
                    <ItemActions
                        onItemCopy={onItemCopy}
                        onItemLock={onItemLock ? handleLockItem : undefined}
                        onItemDelete={onItemDelete}
                        index={index}
                        item={item}
                        open={actionMenuOpen}
                        onClose={() => setActionMenuOpen(false)}
                        onOpen={() => setActionMenuOpen(true)}
                        onRename={onItemRename ? () => setRenaming(true) : undefined}
                    />
                )}
            </StyledListItemButton>
        </StyledListItem>
    );
};

export type EditableListProps<T> = {
    items: T[];
    templatePages?: T[];
    secondaryItems?: T[];
    secondaryItemsHeading?: string;
    pdfFiles?: { fileName: string; totalPages: number; pages: T[] }[];
    currentItem: T;
    disabled: boolean;
    onItemSelect: ItemCallback<T>;
    defaultName?: string;
    onItemCreate?: () => void;
    onItemRename?: ItemCallback<T>;
    onItemLock?: ItemCallback<T>;
    onItemCopy?: ItemCallback<T>;
    onItemDelete?: ItemCallback<T>;
    onImportPdf?: (errorCb?: (error: any) => void) => void;
    onPdfDelete?: (fileName: string) => void;
    fileStatus: Status;
    fileProgress: number;
};

export const EditableList = <T extends Item>(props: EditableListProps<T>) => {
    const {
        items,
        secondaryItems,
        templatePages,
        pdfFiles = [],
        currentItem,
        secondaryItemsHeading,
        onItemSelect,
        onItemCreate,
        onItemDelete,
        onImportPdf,
        onPdfDelete,
        fileStatus,
        fileProgress,
        ...listItemProps
    } = props;

    const { t } = useTranslation(['whiteboard', 'common']);
    const { whiteboardSessionState } = useContext(WhiteboardSessionContext);
    const { session } = whiteboardSessionState;

    const [modalErrorMessage, setModalErrorMessage] = useState<string>('');

    const handleImportPdf = () => {
        onImportPdf?.((error: any) => {
            // eslint-disable-next-line no-console
            console.error(error);
            setModalErrorMessage(error.message);
        });
    };

    const handleCloseDialog = () => {
        setModalErrorMessage('');
    };

    return (
        <>
            <Dialog open={!!modalErrorMessage} onClose={handleCloseDialog}>
                <DialogContent>
                    <Grid container spacing={2}>
                        <Grid item>
                            <Error fontSize="large" style={{ fill: 'red' }} />
                        </Grid>
                        <Grid item xs>
                            <Box mb={2}>
                                <Typography variant="h5">{t('common:somethingWentWrong')}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="body1">{modalErrorMessage}</Typography>
                            </Box>
                        </Grid>
                    </Grid>
                </DialogContent>

                <DialogActions>
                    <Button onClick={handleCloseDialog} color="primary" variant="contained">
                        {t('common:close')}
                    </Button>
                </DialogActions>
            </Dialog>

            {session?.type !== SessionTypes.Presentation && (
                <>
                    <List>
                        {onItemCreate && <AddPageButton onClick={onItemCreate} />}
                        {onImportPdf && <ImportPdfButton onClick={handleImportPdf} />}

                        {templatePages?.map((item, index) => (
                            <EditableListItem<T>
                                {...listItemProps}
                                item={item}
                                index={index}
                                badgeContent={''}
                                key={`${item.name}-${index}`}
                                name={t('whiteboard:controlBoardName')}
                                selected={currentItem === item}
                                onClick={() => onItemSelect(item)}
                            />
                        ))}

                        {items.map((item, index) => (
                            <EditableListItem<T>
                                {...listItemProps}
                                onItemDelete={items.length > 1 ? onItemDelete : undefined}
                                item={item}
                                index={index}
                                badgeContent={index + 1}
                                key={`${item.name}-${index}`}
                                selected={currentItem === item}
                                onClick={() => onItemSelect(item)}
                            />
                        ))}

                        {secondaryItems && secondaryItems.length > 0 && (
                            <>
                                <Divider component="li" />
                                <li>
                                    <Typography
                                        sx={{ mt: 0.5, ml: 2 }}
                                        color="text.secondary"
                                        display="block"
                                        variant="caption"
                                    >
                                        {secondaryItemsHeading}
                                    </Typography>
                                </li>

                                {secondaryItems.map((item, index) => (
                                    <EditableListItem<T>
                                        {...listItemProps}
                                        onItemDelete={secondaryItems.length > 1 ? onItemDelete : undefined}
                                        item={item}
                                        index={index + items.length}
                                        badgeContent={index + items.length + 1}
                                        key={`${item.name}-${index}`}
                                        selected={currentItem === item}
                                        onClick={() => onItemSelect(item)}
                                    />
                                ))}
                            </>
                        )}
                    </List>
                </>
            )}

            {pdfFiles.map((file) => (
                <List key={file.fileName}>
                    <PdfHeader fileName={file.fileName} onDeleteClick={onPdfDelete} key={`delete-${file.fileName}`} />

                    {file.pages.map((item, index) => (
                        <EditableListItem<T>
                            item={item}
                            index={index + items.length}
                            badgeContent={index + 1}
                            key={`${item.name}-${index}`}
                            selected={currentItem === item}
                            onClick={() => onItemSelect(item)}
                            onItemLock={listItemProps.onItemLock}
                            disabled={listItemProps.disabled}
                        />
                    ))}
                </List>
            ))}

            {fileStatus === Status.InProgress && (
                <List>
                    <StyledListHeader>{t('common:uploadingPdf')}</StyledListHeader>
                    <LinearProgressWithLabel value={fileProgress} />
                </List>
            )}
        </>
    );
};
